import styled, { css, FlattenSimpleInterpolation } from 'styled-components'
import { ButtonColorT, ButtonIconPositionT, ButtonSizeT, ButtonVariantT, IButtonProps } from './button.types'
import { baseTheme } from 'primsell-shared-layer/styles/theme'

type ConfigT = Record<
  ButtonColorT,
  {
    background: string
    beforeBackground: string
    borderColor: string
  }
>

const config: ConfigT = {
  violet: {
    background: baseTheme.gradients.violet,
    beforeBackground: baseTheme.colors.violet,
    borderColor: baseTheme.colors.violet,
  },
  dark: {
    background: baseTheme.gradients.dark,
    beforeBackground: baseTheme.colors.buttonDark,
    borderColor: baseTheme.colors.dark,
  },
  grey: {
    background: baseTheme.gradients.grey,
    beforeBackground: baseTheme.gradients.light,
    borderColor: baseTheme.colors.grey,
  },
  lightGrey: {
    background: '#1b1b1b8a',
    beforeBackground: '#1b1b1b8a',
    borderColor: '#1b1b1b8a',
  },
}

const colorCss = css<{ color?: ButtonColorT }>`
  background: ${({ color = 'violet' }) => config[color].background};

  &::before {
    background: ${({ color = 'violet' }) => config[color].beforeBackground};
  }
`

const computedColorVariantCss = css<{ variant?: ButtonVariantT; color?: ButtonColorT }>`
  ${({ variant = 'filled', color = 'violet' }) => {
    if (variant === 'filled') {
      return colorCss
    }
    if (variant === 'outlined') {
      return css`
        border: 1px solid ${config[color]?.borderColor};
      `
    }
  }}
`

const xxsSizeCss = css`
  min-height: 24px;
  min-width: 24px;
  border-radius: 2px;
  background: rgba(134, 134, 134, 0.7);
  backdrop-filter: blur(2px);
`

const xsSizeCss = css`
  padding: 4px;
  border-radius: 4px;
  background: rgba(134, 134, 134, 0.7);
  backdrop-filter: blur(2px);
`

const smSizeCss = css`
  padding: 6px;
  border-radius: 4px;
  background: rgba(134, 134, 134, 0.7);
  backdrop-filter: blur(2px);
`

const baseSizeCss = css`
  min-width: 90px;
  padding: 0 12px;
  border-radius: 8px;
  min-height: 40px;
`

const mdSizeCss = css`
  min-width: 178px;
  padding: 0 12px;
  border-radius: 8px;
  min-height: 40px;
`

const lgSizeCss = css`
  min-width: 420px;
  padding: 0 56px;
  border-radius: 8px;
  min-height: 40px;
`

const xlSizeCss = css`
  min-width: 592px;
  padding: 0 28px;
  border-radius: 10px;
  min-height: 96px;
`

const sizes: Record<ButtonSizeT, FlattenSimpleInterpolation> = {
  xxs: xxsSizeCss,
  xs: xsSizeCss,
  sm: smSizeCss,
  base: baseSizeCss,
  md: mdSizeCss,
  lg: lgSizeCss,
  xl: xlSizeCss,
}

const iconPositions: Record<ButtonIconPositionT, FlattenSimpleInterpolation> = {
  left: css`
    flex-direction: row;
  `,
  right: css`
    flex-direction: row-reverse;
  `,
}

const hoverCss = css`
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;

    opacity: 0;
    transition: all 0.3s ease-in-out;
  }

  &:not(:disabled) {
    &:hover::before {
      opacity: 1;
    }
  }
`

export const Button = styled.button<IButtonProps>`
  position: relative;
  flex-shrink: 0;

  ${computedColorVariantCss}
  ${({ size = 'base' }) => sizes[size]}
  ${({ fullWidth = false }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
  ${hoverCss}
  
  &:disabled {
    opacity: 0.2;
  }
`

export const Inner = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

export const PositionElementsContainer = styled.div<{ iconPosition?: ButtonIconPositionT }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  ${({ iconPosition = 'left' }) => iconPositions[iconPosition]}

  & span {
    margin: auto 0;
  }
`

export const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`
